// src/pages/admin/StationManagement.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { AuthContext } from '../../components/AuthContext';

// Add CSS animations at the component level
const styles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  @keyframes gradientShift {
    0%, 100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }
`;

const STATES = [
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli',
  'Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Bhutan',
  'Nepal',
  'Bangladesh'
];

// Options for react-select
const stateOptions = STATES.map(state => ({ value: state, label: state }));
const limitOptions = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' }
];

const StationManagement = () => {
  const { user } = useContext(AuthContext);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(false);

  // Sync dark mode with localStorage
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    
    checkDarkMode();
    const interval = setInterval(checkDarkMode, 500);
    return () => clearInterval(interval);
  }, []);

  // Premium select styles with dark mode support
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '48px',
      borderRadius: '0.75rem',
      borderWidth: '2px',
      borderColor: state.isFocused 
        ? (darkMode ? '#3B82F6' : '#8B5CF6')
        : (darkMode ? '#475569' : '#D1D5DB'),
      backgroundColor: darkMode ? '#1E293B' : '#FFFFFF',
      boxShadow: state.isFocused 
        ? (darkMode ? '0 0 0 4px rgba(59, 130, 246, 0.3)' : '0 0 0 4px rgba(139, 92, 246, 0.2)')
        : 'none',
      '&:hover': {
        borderColor: darkMode ? '#3B82F6' : '#8B5CF6'
      },
      transition: 'all 0.3s ease'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.75rem',
      overflow: 'hidden',
      backgroundColor: darkMode ? '#1E293B' : '#FFFFFF',
      border: darkMode ? '2px solid #475569' : '2px solid #E5E7EB',
      boxShadow: darkMode 
        ? '0 10px 15px -3px rgba(0, 0, 0, 0.5)' 
        : '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? (darkMode ? '#3B82F6' : '#8B5CF6')
        : state.isFocused
          ? (darkMode ? '#334155' : '#F3F4F6')
          : (darkMode ? '#1E293B' : '#FFFFFF'),
      color: state.isSelected
        ? '#FFFFFF'
        : (darkMode ? '#F1F5F9' : '#111827'),
      fontWeight: state.isSelected ? '700' : '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:active': {
        backgroundColor: darkMode ? '#2563EB' : '#7C3AED'
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? '#F1F5F9' : '#111827',
      fontWeight: '600'
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? '#64748B' : '#9CA3AF',
      fontWeight: '500'
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? '#F1F5F9' : '#111827'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: darkMode ? '#64748B' : '#9CA3AF',
      '&:hover': {
        color: darkMode ? '#3B82F6' : '#8B5CF6'
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: darkMode ? '#64748B' : '#9CA3AF',
      '&:hover': {
        color: darkMode ? '#EF4444' : '#DC2626'
      }
    })
  };

  // Table/pagination/filter state
  const [stations, setStations] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(5); // items per page
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [loading, setLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    state: '',
    createdBy: user?.user?._id,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Ref for focusing the station name input when editing
  const nameInputRef = useRef(null);

  useEffect(() => {
    fetchStations();
    // eslint-disable-next-line
  }, [currentPage, limit, search, stateFilter]);

  const fetchStations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/stations/paginated`,
        {
          params: {
            page: currentPage,
            limit,
            search,
            stateFilter,
          },
        }
      );
      setStations(response.data.stations);
      setTotal(response.data.total);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch stations', 'error');
    } finally {
      setLoading(false);
    }
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Convert station name to uppercase
    const processedValue = name === 'name' ? value.toUpperCase() : value;
    setForm({ ...form, [name]: processedValue });
  };

  const handleStateChange = (selectedOption) => {
    setForm({ ...form, state: selectedOption ? selectedOption.value : '' });
  };

  const handleStateFilterChange = (selectedOption) => {
    setCurrentPage(1);
    setStateFilter(selectedOption ? selectedOption.value : '');
  };

  const handleLimitChange = (selectedOption) => {
    setCurrentPage(1);
    setLimit(selectedOption ? selectedOption.value : 5);
  };

  const validateForm = (checkDuplicate = true) => {
    if (!form.name || !form.state) {
      Swal.fire('Warning', 'Station name and state are required', 'warning');
      return false;
    }
    if (checkDuplicate && !isEditing) {
      const duplicate = stations.find(
        (st) => st.name.toLowerCase() === form.name.toLowerCase()
      );
      if (duplicate) {
        Swal.fire('Warning', 'Station already exists', 'warning');
        return false;
      }
    }
    return true;
  };

  const handleAddOrUpdateStation = async () => {
    // If editing, skip the "station already exists" check if name hasn’t changed.
    const oldStationName = stations.find((s) => s._id === editingId)?.name;
    const checkDuplicate = !isEditing || form.name !== oldStationName;

    if (!validateForm(checkDuplicate)) return;

    try {
      if (isEditing) {
        // Update station
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/stations/${editingId}`,
          {
            ...form,
            updatedBy: user?.user?._id,
          }
        );
        Swal.fire('Success', 'Station updated successfully', 'success');
      } else {
        // Create station
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/stations`,
          form
        );
        Swal.fire('Success', 'Station created successfully', 'success');
      }
      fetchStations();
      resetForm();
    } catch (error) {
      Swal.fire('Error', 'Failed to save station', 'error');
    }
  };

  const startEditing = (station) => {
    setIsEditing(true);
    setEditingId(station._id);
    setForm({
      name: station.name,
      state: station.state,
      createdBy: station.createdBy,
    });
    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }, 100);
  };

  const resetForm = () => {
    setForm({
      name: '',
      state: '',
      createdBy: user?.user?._id,
    });
    setIsEditing(false);
    setEditingId(null);
  };

  // Export stations to Excel
  const handleExportToExcel = async () => {
    try {
      Swal.fire({
        title: 'Exporting...',
        text: 'Please wait while we prepare your Excel file',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (stateFilter) params.append('stateFilter', stateFilter);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/stations/export?${params.toString()}`,
        {
          responseType: 'blob',
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `stations-export-${currentDate}.xlsx`;
      link.setAttribute('download', filename);

      // Trigger download
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Export Successful!',
        text: `File "${filename}" has been downloaded`,
        timer: 3000,
        showConfirmButton: false
      });

    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'Failed to export stations to Excel. Please try again.',
      });
    }
  };

  // Pagination
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <>
      {/* Inject CSS animations */}
      <style>{styles}</style>
      
      <div className={`min-h-screen transition-all duration-500 ${
        darkMode 
          ? 'bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950' 
          : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
      }`}>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-10">
        {/* Premium Header - Mobile Responsive */}
        <div className={`relative overflow-hidden border rounded-2xl sm:rounded-3xl shadow-2xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8 mb-6 sm:mb-8 lg:mb-10 transition-all duration-500 ${
          darkMode 
            ? 'bg-gradient-to-r from-slate-900/95 via-blue-900/90 to-purple-900/95 border-blue-700/30' 
            : 'bg-white/80 backdrop-blur-xl border-white/60 shadow-purple-200/50'
        }`}>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10 animate-pulse"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
              <div className={`p-2 sm:p-3 lg:p-4 rounded-xl sm:rounded-2xl shadow-lg transform hover:scale-110 transition-all duration-300 flex-shrink-0 ${
                darkMode
                  ? 'bg-gradient-to-br from-blue-600 to-purple-600 shadow-blue-500/50'
                  : 'bg-gradient-to-br from-indigo-500 to-purple-500 shadow-purple-500/30'
              }`} style={{ animation: 'float 3s ease-in-out infinite' }}>
                <svg className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-black tracking-tight ${
                  darkMode 
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400' 
                    : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
                }`}>
                  <span className="hidden sm:inline">Station Management</span>
                  <span className="sm:hidden">Stations</span>
                </h2>
                <p className={`mt-1 text-xs sm:text-sm font-medium ${
                  darkMode ? 'text-blue-300' : 'text-purple-600'
                }`}>
                  <span className="hidden sm:inline">📍 Manage railway stations and locations</span>
                  <span className="sm:hidden">📍 Manage stations</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Premium Filters Section - Mobile Responsive */}
        <div className={`rounded-2xl sm:rounded-3xl shadow-xl border-2 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 transition-all duration-500 ${
          darkMode
            ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/90 border-slate-700/50 shadow-blue-500/10'
            : 'bg-white border-purple-200/60 shadow-purple-500/20'
        }`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
            {/* Search filter */}
            <div className="flex flex-col">
              <label className={`mb-2 sm:mb-3 font-black uppercase tracking-wider text-xs ${
                darkMode ? 'text-blue-400' : 'text-purple-700'
              }`}>🔍 Search</label>
              <input
                type="text"
                placeholder="Search by name..."
                className={`px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border-2 font-medium transition-all focus:outline-none focus:ring-4 shadow-md ${
                  darkMode
                    ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500 focus:ring-blue-500/30 focus:border-blue-500 hover:border-blue-600'
                    : 'bg-white border-purple-300 text-gray-900 placeholder-gray-400 focus:ring-purple-500/30 focus:border-purple-500 hover:border-purple-400'
                }`}
                value={search}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSearch(e.target.value);
                }}
              />
            </div>

            {/* State filter */}
            <div className="flex flex-col">
              <label className={`mb-2 sm:mb-3 font-black uppercase tracking-wider text-xs ${
                darkMode ? 'text-blue-400' : 'text-purple-700'
              }`}>🗺️ Filter by State</label>
              <Select
                options={stateOptions}
                value={stateOptions.find(option => option.value === stateFilter)}
                onChange={(selectedOption) => handleStateFilterChange(selectedOption)}
                placeholder="All States"
                isClearable
                styles={selectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* Items per page */}
            <div className="flex flex-col">
              <label className={`mb-2 sm:mb-3 font-black uppercase tracking-wider text-xs ${
                darkMode ? 'text-blue-400' : 'text-purple-700'
              }`}>📊 Per page</label>
              <Select
                options={limitOptions}
                value={limitOptions.find(option => option.value === limit)}
                onChange={(selectedOption) => handleLimitChange(selectedOption)}
                styles={selectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                isSearchable={false}
              />
            </div>

            {/* Export button */}
            <div className="flex flex-col">
              <label className={`mb-2 sm:mb-3 font-black uppercase tracking-wider text-xs ${
                darkMode ? 'text-blue-400' : 'text-purple-700'
              }`}>📤 Export</label>
              <button
                onClick={handleExportToExcel}
                className={`px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl font-black shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 ${
                  darkMode
                    ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white shadow-emerald-500/50'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-green-500/30'
                }`}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Excel</span>
                <span className="sm:hidden">Export</span>
              </button>
            </div>

            {/* Clear filters */}
            <div className="flex flex-col">
              <label className={`mb-2 sm:mb-3 font-black uppercase tracking-wider text-xs ${
                darkMode ? 'text-blue-400' : 'text-purple-700'
              }`}>🗑️ Clear</label>
              <button
                onClick={() => {
                  setSearch('');
                  setStateFilter('');
                  setCurrentPage(1);
                }}
                className={`px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl font-black shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 ${
                  darkMode
                    ? 'bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 text-white shadow-slate-500/50'
                    : 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white shadow-gray-500/30'
                }`}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="hidden sm:inline">Clear</span>
                <span className="sm:hidden">Reset</span>
              </button>
            </div>
          </div>
        </div>

        {/* Premium Form Section - Mobile Responsive */}
        <div className={`rounded-2xl sm:rounded-3xl shadow-xl border-2 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 transition-all duration-500 ${
          darkMode
            ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/90 border-slate-700/50 shadow-blue-500/10'
            : 'bg-white border-purple-200/60 shadow-purple-500/20'
        }`}>
          <h3 className={`text-lg sm:text-xl lg:text-2xl font-black mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3 ${
            darkMode 
              ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400'
              : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
          }`}>
            <div className={`w-1.5 sm:w-2 h-8 sm:h-10 rounded-full ${isEditing ? 'bg-gradient-to-b from-yellow-500 to-orange-500' : 'bg-gradient-to-b from-blue-500 to-purple-500'}`}></div>
            <span className="hidden sm:inline">{isEditing ? '✏️ Edit Station' : '➕ Add New Station'}</span>
            <span className="sm:hidden">{isEditing ? '✏️ Edit' : '➕ Add'}</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Station Name Input */}
            <div className="flex flex-col">
              <label className={`mb-2 sm:mb-3 font-black uppercase tracking-wider text-xs ${
                darkMode ? 'text-blue-400' : 'text-purple-700'
              }`}>🚉 Station Name</label>
              <input
                ref={nameInputRef}
                type="text"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                placeholder="e.g., BANGALORE STATION"
                className={`px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border-2 font-bold transition-all focus:outline-none focus:ring-4 shadow-md uppercase ${
                  darkMode
                    ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500 focus:ring-blue-500/30 focus:border-blue-500 hover:border-blue-600'
                    : 'bg-white border-purple-300 text-gray-900 placeholder-gray-400 focus:ring-purple-500/30 focus:border-purple-500 hover:border-purple-400'
                }`}
                style={{ textTransform: 'uppercase' }}
            />
          </div>

          {/* State Select */}
          <div className="flex flex-col">
            <label className={`mb-2 sm:mb-3 font-black uppercase tracking-wider text-xs ${
              darkMode ? 'text-blue-400' : 'text-purple-700'
            }`}>📍 State</label>
            <Select
              options={stateOptions}
              value={stateOptions.find(option => option.value === form.state)}
              onChange={handleStateChange}
              placeholder="Select State"
              styles={selectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
              isSearchable
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 lg:col-span-1">
            <button
              onClick={handleAddOrUpdateStation}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl text-white font-black shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 ${
                isEditing
                  ? darkMode
                    ? 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 shadow-yellow-500/50'
                    : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 shadow-yellow-500/30'
                  : darkMode
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-blue-500/50'
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-indigo-500/30'
              } w-full sm:w-auto`}
            >
              {isEditing ? (
                <>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="hidden sm:inline">Update Station</span>
                  <span className="sm:hidden">Update</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="hidden sm:inline">Add Station</span>
                  <span className="sm:hidden">Add</span>
                </>
              )}
            </button>
            {isEditing && (
              <button
                onClick={resetForm}
                className={`px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl text-white font-black shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 ${
                  darkMode
                    ? 'bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 shadow-slate-500/50'
                    : 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 shadow-gray-500/30'
                } w-full sm:w-auto`}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="hidden sm:inline">Cancel</span>
                <span className="sm:hidden">Cancel</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Premium Stations Grid - Mobile Responsive */}
      <div className={`rounded-2xl sm:rounded-3xl shadow-xl border-2 p-4 sm:p-6 lg:p-8 transition-all duration-500 ${
        darkMode
          ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/90 border-slate-700/50 shadow-blue-500/10'
          : 'bg-white border-purple-200/60 shadow-purple-500/20'
      }`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
          <h3 className={`text-lg sm:text-xl lg:text-2xl font-black flex items-center gap-2 sm:gap-3 min-w-0 flex-1 ${
            darkMode 
              ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400'
              : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
          }`}>
            <div className="w-1.5 sm:w-2 h-8 sm:h-10 rounded-full bg-gradient-to-b from-blue-500 to-purple-500 flex-shrink-0"></div>
            <span className="hidden sm:inline">🚉 Stations List</span>
            <span className="sm:hidden">🚉 Stations</span>
            <span className={`ml-1 sm:ml-2 px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-full font-black ${
              darkMode
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'bg-purple-100 text-purple-800 border border-purple-200'
            }`}>
              {total}
            </span>
          </h3>
        </div>
        
        <div>
          {loading ? (
            <div className="flex justify-center items-center py-24">
              <div className={`animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 ${
                darkMode ? 'border-blue-500' : 'border-purple-500'
              }`}></div>
            </div>
          ) : stations.length === 0 ? (
            <div className="text-center py-16 sm:py-24">
              <div className={`mx-auto w-24 h-24 sm:w-32 sm:h-32 rounded-full flex items-center justify-center mb-4 sm:mb-6 ${
                darkMode
                  ? 'bg-gradient-to-br from-slate-800 to-slate-700 shadow-lg shadow-blue-500/20'
                  : 'bg-gradient-to-br from-purple-100 to-pink-100 shadow-lg shadow-purple-500/20'
              }`}>
                <svg className={`w-12 h-12 sm:w-16 sm:h-16 ${darkMode ? 'text-blue-400' : 'text-purple-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className={`text-lg sm:text-2xl font-black mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                No Stations Found
              </p>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                <span className="hidden sm:inline">Add your first station or adjust your search filters</span>
                <span className="sm:hidden">Add a station or adjust filters</span>
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {stations.map((station, index) => (
                <div
                  key={station._id}
                  className={`rounded-xl sm:rounded-2xl border-2 p-4 sm:p-6 transition-all duration-500 transform hover:scale-105 hover:shadow-2xl ${
                    darkMode
                      ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/80 border-blue-500/30 shadow-lg shadow-blue-500/10 hover:border-blue-400/50 hover:shadow-blue-500/20'
                      : 'bg-white border-purple-200 shadow-lg hover:border-purple-400 hover:shadow-purple-500/20'
                  }`}
                  style={{
                    animation: `fadeIn 0.6s ease-out ${index * 0.1}s both`,
                  }}
                >
                  {/* Station Name */}
                  <div className="mb-3 sm:mb-4">
                    <h4 className={`text-lg sm:text-xl font-black mb-2 ${
                      darkMode
                        ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400'
                        : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600'
                    }`}>
                      {station.name}
                    </h4>
                    <div className={`h-1 rounded-full ${
                      darkMode
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500'
                        : 'bg-gradient-to-r from-indigo-500 to-purple-500'
                    }`} style={{ width: '50px' }}></div>
                  </div>

                  {/* State */}
                  <div className="mb-3 sm:mb-4 flex items-center gap-2">
                    <span className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-black flex items-center gap-1 sm:gap-2 ${
                      darkMode
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        : 'bg-purple-100 text-purple-800 border border-purple-200'
                    }`}>
                      📍 {station.state}
                    </span>
                  </div>

                  {/* Station ID */}
                  <div className="mb-4 sm:mb-6">
                    <span className={`text-xs font-bold uppercase tracking-wider ${
                      darkMode ? 'text-slate-400' : 'text-gray-500'
                    }`}>
                      ID: {station.stationId || station._id}
                    </span>
                  </div>

                  {/* Edit Button */}
                  <button
                    onClick={() => startEditing(station)}
                    className={`w-full px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl text-white font-black shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 ${
                      darkMode
                        ? 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 shadow-yellow-500/50'
                        : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 shadow-yellow-500/30'
                    }`}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="hidden sm:inline">Edit Station</span>
                    <span className="sm:hidden">Edit</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Premium Pagination Controls - Mobile Responsive */}
      <div className={`rounded-2xl sm:rounded-3xl shadow-xl border-2 p-4 sm:p-6 mt-6 sm:mt-8 transition-all duration-500 ${
        darkMode
          ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/90 border-slate-700/50 shadow-blue-500/10'
          : 'bg-white border-purple-200/60 shadow-purple-500/20'
      }`}>
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className={`inline-flex items-center px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl font-black shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 disabled:transform-none disabled:shadow-none disabled:cursor-not-allowed ${
              currentPage === 1
                ? darkMode
                  ? 'bg-slate-800 text-slate-600 border-2 border-slate-700'
                  : 'bg-gray-200 text-gray-400 border-2 border-gray-300'
                : darkMode
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-blue-500/50'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-indigo-500/30'
            }`}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Prev</span>
          </button>
          
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <div className={`px-3 sm:px-5 py-2 sm:py-3 rounded-lg sm:rounded-xl shadow-lg border-2 ${
              darkMode
                ? 'bg-slate-800 border-blue-500/30 text-blue-400'
                : 'bg-white border-purple-200 text-purple-600'
            }`}>
              <span className="text-xs sm:text-sm font-black">
                <span className="hidden sm:inline">Page </span>
                <span className={`text-sm sm:text-lg ${darkMode ? 'text-blue-300' : 'text-purple-700'}`}>{currentPage}</span>
                <span className="hidden sm:inline"> of </span>
                <span className="sm:hidden">/</span>
                <span className={`text-sm sm:text-lg ${darkMode ? 'text-blue-300' : 'text-purple-700'}`}>{totalPages}</span>
              </span>
            </div>
            <div className={`px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl font-black text-xs sm:text-sm ${
              darkMode
                ? 'bg-blue-500/20 text-blue-400 border-2 border-blue-500/30'
                : 'bg-purple-100 text-purple-800 border-2 border-purple-200'
            }`}>
              <span className="hidden sm:inline">Total: {total} items</span>
              <span className="sm:hidden">{total} total</span>
            </div>
          </div>
          
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`inline-flex items-center px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl font-black shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 disabled:transform-none disabled:shadow-none disabled:cursor-not-allowed ${
              currentPage === totalPages || totalPages === 0
                ? darkMode
                  ? 'bg-slate-800 text-slate-600 border-2 border-slate-700'
                  : 'bg-gray-200 text-gray-400 border-2 border-gray-300'
                : darkMode
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-blue-500/50'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-indigo-500/30'
            }`}
          >
            <span className="hidden sm:inline">Next</span>
            <span className="sm:hidden">Next</span>
            <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-1 sm:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
        </div>
      </div>
    </>
  );
};

export default StationManagement;
